/**
 * 平台检测工具
 * 用于判断当前运行环境：PC、小程序、APP
 * 返回值：0 = PC, 1 = 小程序, 2 = APP
 */

/**
 * 获取 User Agent
 */
function getUserAgent(): string {
  return navigator.userAgent || ''
}

/**
 * 检测是否为微信小程序
 */
function isWechatMiniProgram(): boolean {
  const ua = getUserAgent().toLowerCase()
  
  // 微信小程序的 User Agent 包含 miniprogram
  if (/miniprogram/i.test(ua)) {
    return true
  }
  
  // 通过 window.__wxjs_environment 判断
  if (typeof window !== 'undefined') {
    // @ts-ignore
    if (window.__wxjs_environment === 'miniprogram') {
      return true
    }
    
    // 通过 wx.miniProgram 判断
    // @ts-ignore
    if (window.wx && window.wx.miniProgram) {
      return true
    }
  }
  
  return false
}

/**
 * 检测是否为支付宝小程序
 */
function isAlipayMiniProgram(): boolean {
  const ua = getUserAgent().toLowerCase()
  
  // 支付宝小程序
  if (/alipayclient/i.test(ua) && /miniprogram/i.test(ua)) {
    return true
  }
  
  // 通过 my 对象判断
  if (typeof window !== 'undefined') {
    // @ts-ignore
    if (window.my && window.my.env) {
      return true
    }
  }
  
  return false
}

/**
 * 检测是否为抖音小程序
 */
function isDouyinMiniProgram(): boolean {
  const ua = getUserAgent().toLowerCase()
  
  // 抖音小程序
  if (/toutiaomicroapp/i.test(ua)) {
    return true
  }
  
  // 通过 tt 对象判断
  if (typeof window !== 'undefined') {
    // @ts-ignore
    if (window.tt && window.tt.miniProgram) {
      return true
    }
  }
  
  return false
}

/**
 * 检测是否为百度小程序
 */
function isBaiduMiniProgram(): boolean {
  const ua = getUserAgent().toLowerCase()
  
  // 百度小程序
  if (/swan/i.test(ua)) {
    return true
  }
  
  // 通过 swan 对象判断
  if (typeof window !== 'undefined') {
    // @ts-ignore
    if (window.swan && window.swan.webView) {
      return true
    }
  }
  
  return false
}

/**
 * 检测是否为QQ小程序
 */
function isQQMiniProgram(): boolean {
  const ua = getUserAgent().toLowerCase()
  
  // QQ小程序
  if (/qq/i.test(ua) && /miniprogram/i.test(ua)) {
    return true
  }
  
  // 通过 qq 对象判断
  if (typeof window !== 'undefined') {
    // @ts-ignore
    if (window.qq && window.qq.miniProgram) {
      return true
    }
  }
  
  return false
}

/**
 * 检测是否为任意小程序环境
 */
function isMiniProgram(): boolean {
  return (
    isWechatMiniProgram() ||
    isAlipayMiniProgram() ||
    isDouyinMiniProgram() ||
    isBaiduMiniProgram() ||
    isQQMiniProgram()
  )
}

/**
 * 检测是否为 Capacitor APP
 * 注意：开发环境中 window.Capacitor 可能存在，但不代表是真正的 APP
 */
function isCapacitorApp(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  
  // @ts-ignore
  const capacitor = window.Capacitor
  
  if (!capacitor) {
    return false
  }
  
  // 检查是否为原生平台（不是 web）
  try {
    // @ts-ignore
    const platform = capacitor.getPlatform?.()
    // 只有 ios 或 android 才是真正的 APP，web 平台不算
    return platform === 'ios' || platform === 'android'
  } catch (e) {
    return false
  }
}

/**
 * 检测是否为 iOS APP
 */
function isIOSApp(): boolean {
  // 方法1: 检查自定义的 User Agent 标识（最可靠）
  const ua = getUserAgent().toLowerCase()
  if (/iosappmessenger/i.test(ua)) {
    return true
  }
  
  // 方法2: 通过 Capacitor API 判断平台
  if (typeof window !== 'undefined') {
    try {
      // @ts-ignore
      const platform = window.Capacitor?.getPlatform?.()
      if (platform === 'ios') {
        return true
      }
    } catch (e) {
      // 忽略错误
    }
  }
  
  return false
}

/**
 * 检测是否为 Android APP
 */
function isAndroidApp(): boolean {
  // 方法1: 检查自定义的 User Agent 标识（最可靠）
  const ua = getUserAgent().toLowerCase()
  if (/androidappmessenger/i.test(ua)) {
    return true
  }
  
  // 方法2: 通过 Capacitor API 判断平台
  if (typeof window !== 'undefined') {
    try {
      // @ts-ignore
      const platform = window.Capacitor?.getPlatform?.()
      if (platform === 'android') {
        return true
      }
    } catch (e) {
      // 忽略错误
    }
  }
  
  return false
}

/**
 * 检测是否为 APP（原生应用）
 * 必须满足以下条件之一：
 * 1. User Agent 包含自定义标识（Iosappmessenger 或 Androidappmessenger）
 * 2. Capacitor.getPlatform() 返回 ios 或 android（不是 web）
 */
function isApp(): boolean {
  // 优先检查 User Agent，这是最可靠的方式
  const ua = getUserAgent().toLowerCase()
  if (/iosappmessenger/i.test(ua) || /androidappmessenger/i.test(ua)) {
    return true
  }
  
  // 其次检查 Capacitor 平台
  return isCapacitorApp()
}

/**
 * 检测当前平台类型
 * @returns {number} 返回 0 = PC, 1 = 小程序, 2 = APP
 */
export function detectPlatform(): number {
  // 优先检测小程序
  if (isMiniProgram()) {
    return 1
  }
  
  // 检测 APP
  if (isApp()) {
    return 2
  }
  
  // 默认为 PC（包括PC浏览器和移动端浏览器）
  return 0
}

/**
 * 判断是否为 PC 端
 */
export function isPC(): boolean {
  return detectPlatform() === 0
}

/**
 * 判断是否为小程序
 */
export function isMiniProgramEnv(): boolean {
  return detectPlatform() === 1
}

/**
 * 判断是否为 APP
 */
export function isAppEnv(): boolean {
  return detectPlatform() === 2
}

/**
 * 打印平台信息（用于调试）
 */
export function logPlatformInfo(): void {
  const platform = detectPlatform()
  const platformName = platform === 0 ? 'PC' : platform === 1 ? '小程序' : 'APP'
  console.log('🔍 当前平台:', platformName, `(${platform})`)
  console.log('User Agent:', getUserAgent())
}

// 默认导出
export default {
  detectPlatform,
  isPC,
  isMiniProgramEnv,
  isAppEnv,
  logPlatformInfo,
}
